import { IdPathParameters, SaveOperation } from '@infuse/api/common';
import { UpdateRole } from '@infuse/iam/role/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericUpdateRole = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<UpdateRole, never> | SaveOperation<UpdateRole, IdPathParameters<string>>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; roleId: string; role: UpdateRole }>({
    mutationFn: async ({ parentId, roleId, role }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn({ id: parentId, roleId: roleId }, role);

        notify({
          title: t('iam.roles.header.update_role'),
          message: t('iam.roles.notify.update_role_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.update_role'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.update_role_failed'))
        });
        throw e;
      }
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    }
  });

  return {
    ...mutation
  };
};
